import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../utils/authStorage';
import { useNavigate } from 'react-router-dom';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import Remarks, { validateRemarksSection } from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';
import { confirmAndDelete } from '../../../../utils/confirmAndDelete';

const getAuthConfig = () => {
  if (!hasAuthSession()) {
    console.error('No active auth session found');
    return null;
  }
  return getAuthHeaders();
};

function InputItemsIndigenous() {
  const navigate = useNavigate();
  const dtableRef = useRef(null);

  const {
    itemMasterData,
    filteredItems,
    isLoading: loadingItemMaster,
    debouncedSearch,
    loadMoreData,
    hasMoreData,
    isLoadingMore,
  } = useASIItemMaster();

  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  const [itemData, setItemData] = useState({});
  const [buttonStatus, setButtonStatus] = useState('');
  const [originalValues, setOriginalValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({}); 
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  const [totalProfitandLossAmount, setTotalProfitandLossAmount] = useState({
    SL_No: '',
    Description: '',
    Amount: ''
  });
  const [profitLossAmountDirty, setProfitLossAmountDirty] = useState(false);
  const [profitLossAmountError, setProfitLossAmountError] = useState('');

  const [dropdownStates, setDropdownStates] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({}); 
  const dropdownItemRefs = useRef({}); 
  const justOpenedRef = useRef({}); 

  
  const selectedItemCodes = useMemo(() => {
    const selected = {};
    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && item.itemCode && item.itemCode.toString().trim() !== '') {
        selected[i] = item.itemCode.toString().trim();
      }
    }
    return selected;
  }, [itemData]);

  const getFilteredItemsForRow = useCallback((rowIndex, items) => {
    return items.filter(dropdownItem => {
      const dropdownItemCode = dropdownItem.ASICC_CODE?.toString().trim();
      for (const [index, code] of Object.entries(selectedItemCodes)) {
        if (parseInt(index) !== rowIndex && code === dropdownItemCode) {
          return false; 
        }
      }
      return true;
    });
  }, [selectedItemCodes]);

 
  const selectedItemDescriptions = useMemo(() => {
    const descriptions = [];

    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && item.itemDesc && item.itemDesc.toString().trim() !== '') {
        descriptions.push(item.itemDesc.toString().trim());
      }
    }

    const fixedItems = [
      'Other basic items(indigenous)',
      'Non-basic Chemicals-All kinds',
      'Packing items',
      'Electricity own generated',
      'Electricity purchased & consumed',
      'Petrol,Diesel,Oil,Lubricants',
      'Coal Consumed',
      'Gas consumed',
      'Other fuel consumed',
      'Consumable store',
      'Any additional requirement of electricity(unmet demand)'
    ];

    fixedItems.forEach(fixedItem => {
      if (!descriptions.includes(fixedItem)) {
        descriptions.push(fixedItem);
      }
    });

    return descriptions;
  }, [itemData]);

  // Total Basic Items(1 to 11) – Quantity Consumed = sum of first 11 rows (indices 0–10)
  const totalBasicItemsQtyConsumed = useMemo(() => {
    let sum = 0;
    for (let i = 0; i <= 10; i++) {
      const item = itemData[i];
      if (item?.qtyConsumed !== undefined && item?.qtyConsumed !== '') {
        const val = parseFloat(item.qtyConsumed);
        if (!Number.isNaN(val)) sum += val;
      }
    }
    return sum;
  }, [itemData]);

  // Total Basic Items(1 to 11) – Purchase Value = sum of first 11 rows (indices 0–10)
  const totalBasicItemsPurchaseValue = useMemo(() => {
    let sum = 0;
    for (let i = 0; i <= 10; i++) {
      const item = itemData[i];
      if (item?.purchaseValue !== undefined && item?.purchaseValue !== '') {
        const val = parseFloat(item.purchaseValue);
        if (!Number.isNaN(val)) sum += val;
      }
    }
    return sum;
  }, [itemData]);

  // Total Non-basic items(13 to 21) – Quantity Consumed & Purchase Value = sum of rows 13–21 (indices 12–20)
  const totalNonBasicItemsQtyConsumed = useMemo(() => {
    let sum = 0;
    for (let i = 12; i <= 20; i++) {
      const item = itemData[i];
      if (item?.qtyConsumed !== undefined && item?.qtyConsumed !== '') {
        const val = parseFloat(item.qtyConsumed);
        if (!Number.isNaN(val)) sum += val;
      }
    }
    return sum;
  }, [itemData]);

  const totalNonBasicItemsPurchaseValue = useMemo(() => {
    let sum = 0;
    for (let i = 12; i <= 20; i++) {
      const item = itemData[i];
      if (item?.purchaseValue !== undefined && item?.purchaseValue !== '') {
        const val = parseFloat(item.purchaseValue);
        if (!Number.isNaN(val)) sum += val;
      }
    }
    return sum;
  }, [itemData]);

  // Total Input(12 + 22) = Row 12 (Total Basic 1–11) + Row 22 (Total Non-basic 13–21)
  const totalInputQtyConsumed = useMemo(() => totalBasicItemsQtyConsumed + totalNonBasicItemsQtyConsumed, [totalBasicItemsQtyConsumed, totalNonBasicItemsQtyConsumed]);
  const totalInputPurchaseValue = useMemo(() => totalBasicItemsPurchaseValue + totalNonBasicItemsPurchaseValue, [totalBasicItemsPurchaseValue, totalNonBasicItemsPurchaseValue]);

  // Auto-fill Profit & Loss Amount from Total Basic Items Purchase Value
  useEffect(() => {
    if (profitLossAmountDirty) return;
    setTotalProfitandLossAmount(prev => ({
      ...prev,
      Amount: totalBasicItemsPurchaseValue ? String(totalBasicItemsPurchaseValue) : ''
    }));
    setProfitLossAmountError('');
  }, [totalBasicItemsPurchaseValue, profitLossAmountDirty]);

  const toggleDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => {
      const newState = {};
      const isCurrentlyOpen = prev[key] || false;
      newState[key] = !isCurrentlyOpen;
      return newState;
    });
    setSelectedIndex(prev => {
      const currentState = prev[key];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [key]: -1 };
      }
      return prev;
    });
  };

  const openDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => {
      if (prev[key]) {
        return prev;
      }
      const newState = {};
      newState[key] = true;
      justOpenedRef.current[key] = true;
      setTimeout(() => {
        justOpenedRef.current[key] = false;
      }, 300);
      return newState;
    });
    setSelectedIndex(prev => ({ ...prev, [key]: -1 }));
  };

  const closeDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => ({
      ...prev,
      [key]: false
    }));
    setSelectedIndex(prev => ({ ...prev, [key]: -1 }));
  };

  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };



  const itemCodeArray = ['9920100', '9990100', '9920300', '9990800', '9990400'
    , '9990500', '9990600', '9990700', '9990900', '9920400', '9922000', '9992000', '9993000',
    '9999900'];



  const handleSearchChange = (value) => {
    debouncedSearch(value);
  };



  const fetchExistingData = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for fetching data');
      dataLoadedRef.current = true;
      return;
    }

    console.log('Starting API call for get-data with DSL:', dslNo, 'Year:', surveyYear);
    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        console.error('Authentication token not found for fetching data');
        showLoading(false);
        return;
      }

      const yearOnly = surveyYear ? surveyYear.split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      console.log('Fetching existing data with payload:', payload);

      const response = await axios.post(`${API_URL}api/input-items-indigenous/get-data`, payload, currentConfig);

      if (response.data && response.data.status && response.data.data) {
        console.log('Data fetched successfully:', response.data.data);

        const apiData = response.data.data;
        const mappedData = {};
        const userData = [];
        const systemData = [];

        const specialItemCodes = ['9920100', '9990100', '9920300', '9990800', '9990400',
          '9990500', '9990600', '9990700', '9990900', '9920400',
          '9922000', '9992000', '9993000', '9999900'];

        apiData.forEach((item) => {
          const itemCodeStr = item.item_code?.toString() || '';
          const isSystemItem = specialItemCodes.includes(itemCodeStr);

          if (isSystemItem) {
            systemData.push(item);
          } else {
            userData.push(item);
          }
        });

        userData.forEach((item, index) => {
          if (index < 10) {
            const qtyConsumed = item.qty_consumed?.toString() || '';
            const purchaseValue = item.purchase_value?.toString() || '';
            mappedData[index] = {
              id: index,
              dslNo: item.dsl_no?.toString() || '',
              surveyYear: item.survey_year?.toString() || '',
              itemDesc: item.item_desc || '',
              itemCode: item.item_code?.toString() || '',
              unitOfQty: item.unit_of_qty || '',
              qtyConsumed,
              purchaseValue,
              ratePerUnit: calcRatePerUnit(purchaseValue, qtyConsumed) || item.rate_per_unit?.toString() || '',
              statusFlag: item.status_flag || '',
              saveOrderBy: index + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          }
        });

        for (let i = userData.length; i < 10; i++) {
          mappedData[i] = {
            id: i,
            dslNo: dslNo,
            surveyYear: surveyYear,
            itemDesc: '',
            itemCode: '',
            unitOfQty: '',
            qtyConsumed: '',
            purchaseValue: '',
            ratePerUnit: '',
            statusFlag: '',
            saveOrderBy: i + 1,
            itemCodeClass: 'textBox_back_color_white'
          };
        }

        specialItemCodes.forEach((code, index) => {
          const actualIndex = 10 + index;
          const systemItem = systemData.find(item => item.item_code?.toString() === code);

          if (systemItem) {
            const qtyConsumed = systemItem.qty_consumed?.toString() || '';
            const purchaseValue = systemItem.purchase_value?.toString() || '';
            mappedData[actualIndex] = {
              id: actualIndex,
              dslNo: systemItem.dsl_no?.toString() || dslNo,
              surveyYear: systemItem.survey_year?.toString() || surveyYear,
              itemDesc: systemItem.item_desc || '',
              itemCode: systemItem.item_code?.toString() || '',
              unitOfQty: systemItem.unit_of_qty || '',
              qtyConsumed,
              purchaseValue,
              ratePerUnit: calcRatePerUnit(purchaseValue, qtyConsumed) || systemItem.rate_per_unit?.toString() || '',
              statusFlag: systemItem.status_flag || '',
              saveOrderBy: actualIndex + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          } else {
            const defaultSpecialItems = [
              { itemDesc: 'Other basic items(indigenous)', itemCode: '9920100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '', ratePerUnit: '0' },
              { itemDesc: 'Total Basic Items(1 to 11)', itemCode: '9990100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '', ratePerUnit: '0' },
              { itemDesc: 'Non-basic Chemicals-All kinds', itemCode: '9920300', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Packing items', itemCode: '9990800', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Electricity own generated', itemCode: '9990400', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Electricity purchased & consumed', itemCode: '9990500', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Petrol,Diesel,Oil,Lubricants', itemCode: '9990600', unitOfQty: 'Litre', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Coal Consumed', itemCode: '9990700', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Gas consumed', itemCode: '9990900', unitOfQty: 'Cubic Meter', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Other fuel consumed', itemCode: '9920400', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Consumable store', itemCode: '9922000', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Total Non-basic items(13 to 21)', itemCode: '9992000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
              { itemDesc: 'Total Input(12 + 22)', itemCode: '9993000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
              { itemDesc: 'Any additional requirement of electricity(unmet demand)', itemCode: '9999900', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' }
            ];

            const defaultItem = defaultSpecialItems[index];
            const defaultRate = calcRatePerUnit(defaultItem.purchaseValue, defaultItem.qtyConsumed);
            mappedData[actualIndex] = {
              id: actualIndex,
              dslNo: dslNo,
              surveyYear: surveyYear,
              itemDesc: defaultItem.itemDesc,
              itemCode: defaultItem.itemCode,
              unitOfQty: defaultItem.unitOfQty,
              qtyConsumed: defaultItem.qtyConsumed,
              purchaseValue: defaultItem.purchaseValue,
              ratePerUnit: defaultRate || defaultItem.ratePerUnit,
              statusFlag: '',
              saveOrderBy: actualIndex + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          }
        });

        setItemData(mappedData);

        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            let blockLetter = '';
            if (remark.block) {
              blockLetter = remark.block.replace(/^Block\s+/i, '').trim();
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columns_name || remark.columnsName || '',
              items: remark.items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: remark.created_date || remark.createdDate || '',
              remarksBy: remark.updated_by || remark.updatedBy || remark.created_by || '',
              role: remark.updated_by || remark.updatedBy || ''
            };
          });
          setRemarksList(mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        // Set default remarksData
        setRemarksData({
          remarksBlock: '',
          remarksItems: '',
          remarks: ''
        });

        if (response.data.totalProfitandLossAmount) {
          const profitLossData = response.data.totalProfitandLossAmount;
          setTotalProfitandLossAmount({
            SL_No: profitLossData.SL_No !== undefined ? profitLossData.SL_No : (profitLossData.sl_No !== undefined ? profitLossData.sl_No : ''),
            Description: profitLossData.Description || profitLossData.description || '',
            Amount: profitLossData.Amount !== undefined && profitLossData.Amount !== null ? profitLossData.Amount.toString() : (profitLossData.amount !== undefined && profitLossData.amount !== null ? profitLossData.amount.toString() : '')
          });
        } else {
          setTotalProfitandLossAmount({
            SL_No: '',
            Description: '',
            Amount: ''
          });
        }

        dataLoadedRef.current = true; 
        console.log('API data loaded successfully, dataLoadedRef set to true');
        console.log('Remarks loaded:', mappedRemarksList);
      } else {
        console.log('No data found or invalid response');
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
        dataLoadedRef.current = true; 
        console.log('No data found, initialized with default data, dataLoadedRef set to true');
      }
    } catch (error) {
      console.error('Error fetching data from API:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 404) {
        console.log('No existing data found, initializing with default data');
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      } else {
        toast.error('Failed to load existing data. Using default form.', messages.COLOR_RED);
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      }
      dataLoadedRef.current = true; 
      console.log('Error occurred, initialized with default data, dataLoadedRef set to true');
    } finally {
      showLoading(false);
    }
  }, []); 

  // Use ref to track if data has been loaded to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  const fetchHeaderData = useCallback(async () => {
    if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
      console.log('Data already loaded, loading in progress, or API call already made - skipping');
      return;
    }

    try {
      const balanceSheetData = localStorage.getItem('balanceSheetFormData');

      if (balanceSheetData) {
        const parsedData = JSON.parse(balanceSheetData);

        const headerInfo = {
          despatchSlNo: parsedData.dslNo || '',
          permanentSlNo: parsedData.pslNo || '',
          companyName: parsedData.nameUnit || '',
          dslNo: parsedData.dslNo || '',
          surveyYear: parsedData.year || ''
        };

        setHeaderData(headerInfo);

        if (headerInfo.dslNo && headerInfo.surveyYear) {
          apiCallMadeRef.current = true; 
          isLoadingRef.current = true;
          await fetchExistingData(headerInfo.dslNo, headerInfo.surveyYear);
          isLoadingRef.current = false;
        } else {
          initializeTableData();
        }
      } else {
        console.log('No balanceSheet data found in localStorage');
        setHeaderData({
          despatchSlNo: 'DSL No. not set',
          permanentSlNo: 'PSL No. not set',
          companyName: 'Company name not set',
          dslNo: '',
          surveyYear: ''
        });
        initializeTableData();
      }
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setHeaderData({
        despatchSlNo: 'Error loading data',
        permanentSlNo: 'Error loading data',
        companyName: 'Error loading data',
        dslNo: '',
        surveyYear: ''
      });
      initializeTableData();
    }
  }, []);

  useEffect(() => {
    fetchHeaderData();
  }, [fetchHeaderData]); 


  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target;
      const clickedDropdownContainer = clickedElement.closest('.position-relative');
      const isDropdownMenu = clickedElement.closest('.position-absolute.bg-white.border.rounded.shadow-sm');
      const isInputField = clickedElement.tagName === 'INPUT' && clickedElement.type === 'text';
      const openDropdowns = Object.keys(dropdownStates).filter(key => dropdownStates[key]);

      if (openDropdowns.length > 0) {
        if (isInputField) {
          let isInsideOpenDropdown = false;

          if (clickedDropdownContainer) {
            const visibleDropdown = clickedDropdownContainer.querySelector('.position-absolute.bg-white.border.rounded.shadow-sm');
            if (visibleDropdown && visibleDropdown.offsetParent !== null) {
              isInsideOpenDropdown = true;
            }
          }

          if (!isInsideOpenDropdown) {
            closeAllDropdowns();
          }
        } else if (!clickedDropdownContainer && !isDropdownMenu) {
          closeAllDropdowns();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownStates]);




  // Initialize table with default data
  const initializeTableData = () => {
    const initialData = {};

    for (let i = 0; i < 10; i++) {
      initialData[i] = {
        id: i,
        dslNo: headerData.despatchSlNo,
        surveyYear: '2023-24',
        itemDesc: '',
        itemCode: '',
        unitOfQty: '',
        qtyConsumed: '',
        purchaseValue: '',
        ratePerUnit: '',
        statusFlag: '',
        saveOrderBy: 1,
        itemCodeClass: 'textBox_back_color_white'
      };
    }

    // Special items from 11-24 (system rows)
    const specialItems = [
      { itemDesc: 'Other basic items(indigenous)', itemCode: '9920100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '1896774347', ratePerUnit: '0' },
      { itemDesc: 'Total Basic Items(1 to 11)', itemCode: '9990100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '2442955149', ratePerUnit: '0' },
      { itemDesc: 'Non-basic Chemicals-All kinds', itemCode: '9920300', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Packing items', itemCode: '9990800', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Electricity own generated', itemCode: '9990400', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Electricity purchased & consumed', itemCode: '9990500', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Petrol,Diesel,Oil,Lubricants', itemCode: '9990600', unitOfQty: 'Litre', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Coal Consumed', itemCode: '9990700', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Gas consumed', itemCode: '9990900', unitOfQty: 'Cubic Meter', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Other fuel consumed', itemCode: '9920400', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Consumable store', itemCode: '9922000', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Total Non-basic items(13 to 21)', itemCode: '9992000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
      { itemDesc: 'Total Input(12 + 22)', itemCode: '9993000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
      { itemDesc: 'Any additional requirement of electricity(unmet demand)', itemCode: '9999900', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' }
    ];

    // Add special items starting from index 10 (Rate Per Unit auto-calculated when pv & qty available)
    specialItems.forEach((item, index) => {
      const actualIndex = 10 + index;
      const ratePerUnit = calcRatePerUnit(item.purchaseValue, item.qtyConsumed) || item.ratePerUnit;
      initialData[actualIndex] = {
        id: actualIndex,
        dslNo: headerData.despatchSlNo,
        surveyYear: '2023-24',
        itemDesc: item.itemDesc,
        itemCode: item.itemCode,
        unitOfQty: item.unitOfQty,
        qtyConsumed: item.qtyConsumed,
        purchaseValue: item.purchaseValue,
        ratePerUnit,
        statusFlag: '',
        saveOrderBy: actualIndex + 1,
        itemCodeClass: 'textBox_back_color_white'
      };
    });

    setItemData(initialData);
    dataLoadedRef.current = true; 
    console.log('Table data initialized, dataLoadedRef set to true');
  };

  // validation functions
  const validateNumericInput = (value, fieldName, maxLength = null, allowDecimal = true, allowNegative = false) => {
    let sanitized = value;

    if (allowDecimal) {
      sanitized = value.replace(/[^\d.-]/g, '');
      const parts = sanitized.split('.');
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
      }
      if (!allowNegative) {
        sanitized = sanitized.replace(/-/g, '');
      } else {
        sanitized = sanitized.replace(/^-?/, sanitized.startsWith('-') ? '-' : '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
      }
    } else {
      if (allowNegative) {
        sanitized = value.replace(/[^\d-]/g, '');
        sanitized = sanitized.replace(/^-?/, sanitized.startsWith('-') ? '-' : '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
      } else {
        sanitized = value.replace(/[^\d]/g, '');
      }
    }

    if (maxLength && sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength);
    }

    return sanitized;
  };

  const validateItemCode = (value) => {
    const sanitized = value.replace(/[^\d]/g, '');
    return sanitized.substring(0, 7);
  };

  const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  };

  const validateNumberRange = (value, min = null, max = null) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return null; 
    }
    if (min !== null && numValue < min) {
      return `Value must be at least ${min}`;
    }
    if (max !== null && numValue > max) {
      return `Value must not exceed ${max}`;
    }
    return null;
  };

  // Auto-calculate Rate Per Unit = Purchase Value / Quantity Consumed (when both available)
  const calcRatePerUnit = (purchaseValue, qtyConsumed) => {
    const pv = purchaseValue !== undefined && purchaseValue !== null && purchaseValue !== '' ? parseFloat(purchaseValue) : NaN;
    const qty = qtyConsumed !== undefined && qtyConsumed !== null && qtyConsumed !== '' ? parseFloat(qtyConsumed) : NaN;
    if (Number.isFinite(pv) && Number.isFinite(qty) && qty > 0) {
      return (pv / qty).toFixed(2);
    }
    return '';
  };

  const handleInputChange = (index, field, value) => {
    const newFormData = { ...itemData };
    const newErrors = { ...validationErrors };
    let sanitizedValue = value;
    let error = null;

    switch (field) {
      case 'itemCode':
        sanitizedValue = validateItemCode(value);
        if (sanitizedValue.length > 0 && sanitizedValue.length < 7) {
          error = 'Item Code must be 7 digits';
        }
        break;

      case 'qtyConsumed':
        sanitizedValue = validateNumericInput(value, 'Quantity Consumed', 18, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'purchaseValue':
        sanitizedValue = validateNumericInput(value, 'Purchase Value', 12, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'ratePerUnit':
        sanitizedValue = validateNumericInput(value, 'Rate Per Unit', 18, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'itemDesc':
        sanitizedValue = value.substring(0, 200);
        break;

      case 'unitOfQty':
        sanitizedValue = value.substring(0, 19);
        break;

      default:
        sanitizedValue = value;
    }

    newFormData[index][field] = sanitizedValue;

    // Recalculate Rate Per Unit = Purchase Value / Quantity Consumed when either changes (same as InputItemsImported)
    if (field === 'purchaseValue' || field === 'qtyConsumed') {
      const pv = newFormData[index].purchaseValue;
      const qty = newFormData[index].qtyConsumed;
      const pvNum = pv !== '' && pv != null ? parseFloat(pv) : NaN;
      const qtyNum = qty !== '' && qty != null ? parseFloat(qty) : NaN;
      if (Number.isFinite(pvNum) && Number.isFinite(qtyNum) && qtyNum > 0) {
        newFormData[index].ratePerUnit = (pvNum / qtyNum).toFixed(2);
      } else {
        newFormData[index].ratePerUnit = '';
      }
    }

    setItemData(newFormData);

    if (error) {
      newErrors[`${index}-${field}`] = error;
    } else {
      delete newErrors[`${index}-${field}`];
    }
    setValidationErrors(newErrors);
  };

  const handleItemDescriptionChange = (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ITEM_NAME === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          itemDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        itemDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
    closeDropdown(index, 'itemDesc');
  };

  const handleItemCodeChange = async (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ASICC_CODE === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          itemDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        itemDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
    closeDropdown(index, 'itemCode');
  };

  const handleKeyDown = (e, rowIndex, fieldType, filteredItemsList) => {
    const key = `${rowIndex}-${fieldType}`;
    const currentSelected = selectedIndex[key] !== undefined ? selectedIndex[key] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [key]: newIndex }));
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${key}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [key]: newIndex }));
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${key}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[key] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          if (fieldType === 'itemDesc') {
            handleItemDescriptionChange(rowIndex, filteredItemsList[currentSelected].ITEM_NAME);
          } else if (fieldType === 'itemCode') {
            handleItemCodeChange(rowIndex, filteredItemsList[currentSelected].ASICC_CODE);
          }
        } else if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        break;

      case 'Escape':
        e.preventDefault();
        closeDropdown(rowIndex, fieldType);
        break;

      case 'Tab':
        closeDropdown(rowIndex, fieldType);
        break;

      default:
        if (!dropdownStates[key] && e.key.length === 1) {
          openDropdown(rowIndex, fieldType);
        }
        break;
    }
  };

  const handleOnFocus = (index, field) => {
    if (field === 'itemCode' && !originalValues[index]) {
      const currentValue = itemData[index] ? itemData[index].itemCode : '';
      console.log('Storing original value for index:', index, 'Value:', currentValue);
      setOriginalValues(prev => ({
        ...prev,
        [index]: currentValue
      }));
    }
  };

  const handleOnBlur = async (index, newValue) => {
    const originalValue = originalValues[index] || '';

    console.log('handleOnBlur - index:', index, 'Original Value:', originalValue, 'New Value:', newValue);
    if (originalValue === newValue) {
      console.log('Value unchanged, skipping API call for index:', index);
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      return false;
    }

    let newItemAdded = {
      "id": index,
      "dslNo": headerData.despatchSlNo,
      "surveyYear": "2023-24",
      "itemDesc": "",
      "itemCode": "",
      "unitOfQty": "",
      "qtyConsumed": "",
      "purchaseValue": "",
      "ratePerUnit": "",
      "statusFlag": "",
      "saveOrderBy": 1,
      "itemCodeClass": "textBox_back_color_white",
    };

    if (newValue === '') {
      newItemAdded.itemCode = "";
      handleTableRowData(index, newItemAdded);
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      return false;
    }

    const asicc_code = newValue;
    const newObject = {
      asicc_code: asicc_code
    };

    if (asicc_code.length < 7) {
      newItemAdded.itemCode = "";
      handleTableRowData(index, newItemAdded);
      toast.error("Invalid NPCMS Code.", messages.COLOR_RED);
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      return false;
    }

    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      console.log('Making API call with config:', currentConfig);
      console.log('Request payload:', newObject);
      const response = await axios.post(`${API_URL}api/asi/blockHFetchDataByItemCodeNPCMS`, newObject, currentConfig);
      const { data, status } = response.data;

      if (status) {
        const responseData = data.blockHData;
        newItemAdded = {
          "id": index,
          "dslNo": headerData.despatchSlNo,
          "surveyYear": "2023-24",
          "itemDesc": responseData.itemName,
          "itemCode": asicc_code,
          "unitOfQty": responseData.unitOfQuantity,
          "qtyConsumed": "",
          "purchaseValue": "",
          "ratePerUnit": "",
          "statusFlag": "",
          "saveOrderBy": 1,
          "itemCodeClass": "textBox_back_color_white",
        };
      } else {
        newItemAdded = {
          "id": index,
          "dslNo": headerData.despatchSlNo,
          "surveyYear": "2023-24",
          "itemDesc": '',
          "itemCode": '',
          "unitOfQty": '',
          "qtyConsumed": "",
          "purchaseValue": "",
          "ratePerUnit": "",
          "statusFlag": "",
          "saveOrderBy": 1,
          "itemCodeClass": "textBox_back_color_white",
        };
        toast.error('Item not present - Please check itemcode', messages.COLOR_RED);
      }
      handleTableRowData(index, newItemAdded);

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
    } finally {
      showLoading(false);
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }
  };
  const handleTableRowData = (index, newItemAdded) => {
    setItemData(prevItemData => ({
      ...prevItemData,
      [index]: {
        ...prevItemData[index],
        ...newItemAdded
      }
    }));
    return true;
  };

  /* handleCheckboxChange function commented out - using Action button for deletion
  const handleCheckboxChange = (event, index) => {
    if (event.target.checked) {
      setCheckedItems([...checkedItems, index]);
    } else {
      setCheckedItems(checkedItems.filter((item) => item !== index));
    }
  };
  */

  const removeRowFromState = (index) => {
    const entries = Object.entries(itemData || {})
      .filter(([k]) => Number(k) !== index)
      .sort((a, b) => Number(a[0]) - Number(b[0]));
    const newItemData = {};
    entries.forEach(([, item], i) => {
      newItemData[i] = { ...item, id: i };
    });
    setItemData(newItemData);
  };

  // Clear row at index (blank row) – no reindex; "Other basic items" stays at 11
  const clearRowAtState = (index) => {
    const blankRow = {
      id: index,
      dslNo: headerData.despatchSlNo,
      surveyYear: headerData.surveyYear || '2023-24',
      itemDesc: '',
      itemCode: '',
      unitOfQty: '',
      qtyConsumed: '',
      purchaseValue: '',
      ratePerUnit: '',
      statusFlag: '',
      saveOrderBy: 1,
      itemCodeClass: 'textBox_back_color_white'
    };
    setItemData(prev => ({ ...prev, [index]: blankRow }));
  };

  const handleDeleteSingleRow = async (index) => {
    if (itemCodeArray.includes(itemData[index]?.itemCode)) return; 
    const item = itemData[index];
    const itemDesc = item?.itemDesc || '';
    const itemCode = item?.itemCode || '';
    const message = `Are you sure you want to delete Row ${index + 1}?${itemDesc ? `\n\nItem: ${itemDesc}` : ''}${itemCode ? `\nItem Code: ${itemCode}` : ''}`;

    const surveyYearOnly = (headerData.surveyYear || '').split('-')[0] || '';
    const qtyConsumed = item?.qtyConsumed !== undefined && item?.qtyConsumed !== '' ? parseFloat(item.qtyConsumed) || 0 : 0;
    const purchaseValue = item?.purchaseValue !== undefined && item?.purchaseValue !== '' ? parseFloat(item.purchaseValue) || 0 : 0;

    const deleted = await confirmAndDelete({
      message,
      showLoadingDuringCall: true,
      deleteApiCall: async () => {
        const payload = {
          dsl_no: headerData.despatchSlNo || headerData.dslNo || '',
          survey_year: surveyYearOnly,
          block_type: 'H',
          block_title: 'Input Items Indigenous',
          row_index: index,
          item_desc: itemDesc,
          item_code: (itemCode || '').toString().trim(),
          qty_consumed: qtyConsumed,
          purchase_value: purchaseValue
        };
        return axios.post(`${API_URL}api/balanceSheet/delete-item`, payload, getAuthConfig());
      },
      onSuccess: () => (index < 10 ? clearRowAtState(index) : removeRowFromState(index)),
    });

    if (!deleted) return;
  };

  const addRow = () => {
    const INSERT_AT = 10; 
    const newItem = {
      id: INSERT_AT,
      dslNo: headerData.despatchSlNo,
      surveyYear: headerData.surveyYear || '2023-24',
      itemDesc: '',
      itemCode: '',
      unitOfQty: '',
      qtyConsumed: '',
      purchaseValue: '',
      ratePerUnit: '',
      statusFlag: '',
      saveOrderBy: 1,
      itemCodeClass: 'textBox_back_color_white'
    };
    setItemData(prev => {
      const next = {};
      const keys = Object.keys(prev).map(Number).sort((a, b) => a - b);
      keys.forEach(k => {
        if (k < INSERT_AT) next[k] = { ...prev[k], id: k };
        else next[k + 1] = { ...prev[k], id: k + 1 };
      });
      next[INSERT_AT] = newItem;
      return next;
    });
  };

  const handleReset = () => {
    const newItemData = { ...itemData };
    Object.keys(newItemData).forEach(key => {
      newItemData[key] = {
        ...newItemData[key],
        qtyConsumed: '',
        purchaseValue: '',
        ratePerUnit: ''
      };
    });
    setItemData(newItemData);
  };


  // Function to validate all form data before saving
  const validateFormData = () => {
    const errors = [];

    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && (item.itemDesc || item.itemCode || item.qtyConsumed || item.purchaseValue)) {
        // Validate Item Code if provided
        if (item.itemCode && item.itemCode.length > 0 && item.itemCode.length !== 7) {
          errors.push(`Row ${i + 1}: Item Code must be exactly 7 digits`);
        }

        // Validate Quantity Consumed
        if (item.qtyConsumed) {
          const qty = parseFloat(item.qtyConsumed);
          if (isNaN(qty) || qty < 0) {
            errors.push(`Row ${i + 1}: Quantity Consumed must be a positive number`);
          }
        }

        // Validate Purchase Value
        if (item.purchaseValue) {
          const value = parseFloat(item.purchaseValue);
          if (isNaN(value) || value < 0) {
            errors.push(`Row ${i + 1}: Purchase Value must be a positive number`);
          }
        }

        // If item code is provided, item description should also be provided
        if (item.itemCode && !item.itemDesc) {
          errors.push(`Row ${i + 1}: Item Description is required when Item Code is provided`);
        }
      }
    }

    return errors;
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.dslNo || !headerData.surveyYear) {
      toast.error('DSL No. and Survey Year are required to save data', messages.COLOR_RED);
      return false;
    }

    const remarksValidation = validateRemarksSection(remarksData);
    if (!remarksValidation.valid) {
      toast.error(remarksValidation.message, messages.COLOR_RED);
      return false;
    }

    // Validate form data before saving
    const validationErrors = validateFormData();
    if (validationErrors.length > 0) {
      toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`, messages.COLOR_RED);
      return false;
    }

    // Validate Profit & Loss Amount (user editable)
    const amountStr = (totalProfitandLossAmount.Amount ?? '').toString().trim();
    if (amountStr !== '') {
      if (amountStr.length > 12) {
        const msg = 'Amount must be at most 12 characters';
        setProfitLossAmountError(msg);
        toast.error(msg, messages.COLOR_RED);
        return false;
      }
      if (!/^\d*\.?\d*$/.test(amountStr)) {
        const msg = 'Only numbers allowed in Amount';
        setProfitLossAmountError(msg);
        toast.error(msg, messages.COLOR_RED);
        return false;
      }
      const amountNum = parseFloat(amountStr);
      if (!Number.isFinite(amountNum) || amountNum < 0) {
        const msg = 'Amount must be a non-negative number';
        setProfitLossAmountError(msg);
        toast.error(msg, messages.COLOR_RED);
        return false;
      }
      if (profitLossAmountDirty && totalBasicItemsPurchaseValue) {
        const expected = Number(totalBasicItemsPurchaseValue);
        if (Number.isFinite(expected) && Math.abs(amountNum - expected) > 0.01) {
          const msg = `Amount should match Total Basic Items Purchase Value (${expected})`;
          setProfitLossAmountError(msg);
          toast.error(msg, messages.COLOR_RED);
          return false;
        }
      }
    } else if (!profitLossAmountDirty && totalBasicItemsPurchaseValue) {
      // If user hasn't edited, auto-filled value should be present; keep field error-free.
      setProfitLossAmountError('');
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Extract year only from survey year (e.g., "2024-25" -> "2024")
      const surveyYearOnly = headerData.surveyYear.split('-')[0] || '2024';

      // Prepare data for API - rows 1-10 (user editable) + rows 11-24 (system/special items)
      const dataToSave = [];

      // First 10 rows (user editable)
      for (let i = 0; i < 10; i++) {
        const item = itemData[i];
        if (item && (item.itemDesc || item.itemCode || item.qtyConsumed || item.purchaseValue)) {
          // Sanitize numeric values
          const qtyConsumed = item.qtyConsumed ? parseFloat(item.qtyConsumed) || 0 : 0;
          const purchaseValue = item.purchaseValue ? parseFloat(item.purchaseValue) || 0 : 0;

          // Validate numeric values are positive
          if (qtyConsumed < 0 || purchaseValue < 0) {
            toast.error(`Row ${i + 1}: Values cannot be negative`, messages.COLOR_RED);
            showLoading(false);
            return false;
          }

          const payload = {
            dsl_no: headerData.dslNo,
            survey_year: surveyYearOnly,
            item_code: (item.itemCode || '').toString().trim(),
            item_desc: (item.itemDesc || '').trim(),
            unit_of_qty: (item.unitOfQty || '').trim(),
            qty_consumed: qtyConsumed,
            purchase_value: purchaseValue
          };

          dataToSave.push(payload);
        }
      }

      // Rows 11-24 (indices 10-23) - system/special items - include all so backend gets full data
      for (let i = 10; i <= 23; i++) {
        const item = itemData[i];
        if (item && item.itemCode) {
          const qtyConsumed = item.itemCode === '9990100' ? totalBasicItemsQtyConsumed : (item.itemCode === '9992000' ? totalNonBasicItemsQtyConsumed : (item.itemCode === '9993000' ? totalInputQtyConsumed : (item.qtyConsumed !== undefined && item.qtyConsumed !== '' ? parseFloat(item.qtyConsumed) || 0 : 0)));
          const purchaseValue = item.itemCode === '9990100' ? totalBasicItemsPurchaseValue : (item.itemCode === '9992000' ? totalNonBasicItemsPurchaseValue : (item.itemCode === '9993000' ? totalInputPurchaseValue : (item.purchaseValue !== undefined && item.purchaseValue !== '' ? parseFloat(item.purchaseValue) || 0 : 0)));

          if (qtyConsumed < 0 || purchaseValue < 0) {
            toast.error(`Row ${i + 1}: Values cannot be negative`, messages.COLOR_RED);
            showLoading(false);
            return false;
          }

          dataToSave.push({
            dsl_no: headerData.dslNo,
            survey_year: surveyYearOnly,
            item_code: (item.itemCode || '').toString().trim(),
            item_desc: (item.itemDesc || '').trim(),
            unit_of_qty: (item.unitOfQty || '').trim(),
            qty_consumed: qtyConsumed,
            purchase_value: purchaseValue
          });
        }
      }

      if (dataToSave.length === 0) {
        toast.error('No data to save. Please fill at least one row.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo,
          surveyYear: surveyYearOnly,
          block: 'H',
          columnsName: remarksData.remarksItems || '',
          Items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving data to API:', finalPayload);

      const response = await axios.post(`${API_URL}api/input-items-indigenous/insert`, finalPayload, currentConfig);

      // Check response
      if (response.data && response.data.status) {
        toast.success(`Successfully saved ${dataToSave.length} items to database`, messages.COLOR_RED);

        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.INPUT_ITEMS_INDIGENOUS);
        // Refresh balance sheet validation to update header colors
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  // Function to get proper row number display
  const getRowNumber = (index, totalRows) => {
    // For first 10 rows (1-10)
    if (index < 10) {
      return index + 1;
    }
    // For special rows (11-25) - these are fixed
    else if (index >= totalRows - 14) {
      const specialRowIndex = index - (totalRows - 14);
      return 11 + specialRowIndex;
    }
    // For dynamic rows added by user
    else {
      return index + 1;
    }
  };

  /* Function to check if row should have checkbox - commented out
  const shouldShowCheckbox = (index, totalRows) => {
    // Only first 10 rows and any additional user-added rows should have checkboxes
    return index < 10;
  };
  */

  // Function to check if row is a special/system row
  const isSpecialRow = (index, totalRows) => {
    return index >= totalRows - 14;
  };

  return (
    <>
      <div className="container-fluid py-3">
        {/* Header Section */}
        <HeaderInfo
          title="Input Items - indigenous items consumed"
          icon="fa fa-cubes"
          iconColor="#1976d2"
          titleColor="#1976d2"
          backgroundColor=""
          dispatchSlNo={headerData.despatchSlNo}
          permanentSlNo={headerData.permanentSlNo}
          companyName={headerData.companyName}
          companyPlaceholder="Enter Company Name"
          showBlockCType={false}
          includeTableStyles={false}
        />

        {/* Main Content Area */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                {/* <h6 className="fw-semibold">
                  DSL No: <span className="fw-normal me-5">{headerData.despatchSlNo}</span>
                  Survey Year: <span className="fw-normal">2023-24</span>
                </h6> */}
                <div className="table-responsive asi_block_table">
                  <table cellPadding="0" cellSpacing="0" className="table table-bordered block_c_table" id="velblockh" ref={dtableRef}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Sl. No.</th>
                        <th style={{ textAlign: 'center' }}>Item Description</th>
                        <th style={{ textAlign: 'center' }}>Item Code</th>
                        <th style={{ textAlign: 'center' }}>Unit of Quantity</th>
                        <th style={{ textAlign: 'center' }}>Quantity Consumed</th>
                        <th style={{ textAlign: 'center' }}>Purchase Value (in Rs.)</th>
                        <th style={{ textAlign: 'center' }}>Rate Per Unit (in Rs.)</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: 'center' }}>(1)</th>
                        <th style={{ textAlign: 'center' }}>(2)</th>
                        <th style={{ textAlign: 'center' }}>(3)</th>
                        <th style={{ textAlign: 'center' }}>(4)</th>
                        <th style={{ textAlign: 'center' }}>(5)</th>
                        <th style={{ textAlign: 'center' }}>(6)</th>
                        <th style={{ textAlign: 'center' }}>(7)</th>
                        <th style={{ textAlign: 'center' }}>(8)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(itemData).map((item, i) => {
                        const totalRows = Object.keys(itemData).length;
                        let rowNumber;
                        let isIndex = false;
                        let isDisable = false;

                        // Sequential serial number for all rows (1, 2, 3, ...)
                        rowNumber = i + 1;

                        if (i >= totalRows - 14) {
                          isIndex = true;
                        }

                        if (itemCodeArray.includes(itemData[item.id]?.itemCode) &&
                          i >= totalRows - 14) {
                          isDisable = true;
                        }

                        return (
                          <tr key={i}>
                            <td style={{ textAlign: 'center' }}>
                              {rowNumber}
                              {/* Checkbox commented out - using Action button for deletion
                              {i < 10 && !isIndex &&
                                <>
                                  -
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${i}-id`}
                                    checked={checkedItems.includes(i)}
                                    onChange={(event) => handleCheckboxChange(event, i)}
                                  />
                                </>
                              }
                              */}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {i < 10 ? (
                                // Searchable dropdown for first 10 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={itemData[item.id]?.itemDesc || ''}
                                    placeholder="Item Description"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemDesc', e.target.value);
                                      handleSearchChange(e.target.value);
                                      // Reset selected index when typing
                                      setSelectedIndex(prev => ({ ...prev, [`${item.id}-itemDesc`]: -1 }));
                                    }}
                                    onFocus={(e) => {
                                      // Open dropdown on focus if it's closed (for keyboard navigation)
                                      // Use setTimeout to avoid conflict with mousedown
                                      setTimeout(() => {
                                        if (!dropdownStates[`${item.id}-itemDesc`]) {
                                          openDropdown(item.id, 'itemDesc');
                                        }
                                      }, 0);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const key = `${item.id}-itemDesc`;
                                      // Only toggle if dropdown wasn't just opened by mousedown
                                      if (!justOpenedRef.current[key]) {
                                        toggleDropdown(item.id, 'itemDesc');
                                      } else {
                                        // Clear the flag since we handled it
                                        justOpenedRef.current[key] = false;
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Open dropdown immediately on mousedown to ensure it opens on first click
                                      // This prevents the need for double-click
                                      if (!dropdownStates[`${item.id}-itemDesc`]) {
                                        openDropdown(item.id, 'itemDesc');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      handleKeyDown(e, item.id, 'itemDesc', getFilteredItemsForRow(item.id, filteredItems));
                                    }}
                                  />
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemDesc`] && (() => {
                                    // Filter out already selected items for this row
                                    const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                    return (
                                      <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{
                                          top: '100%',
                                          left: 0,
                                          zIndex: 1000,
                                          maxHeight: '200px',
                                          overflowY: 'auto'
                                        }}
                                      >
                                        {availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => {
                                              const key = `${item.id}-itemDesc`;
                                              const isSelected = selectedIndex[key] === index;
                                              return (
                                                <div
                                                  key={index}
                                                  ref={(el) => {
                                                    if (el) {
                                                      dropdownItemRefs.current[`${key}-${index}`] = el;
                                                    }
                                                  }}
                                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                                  style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                                    outlineOffset: '-2px'
                                                  }}
                                                  onClick={() => {
                                                    handleItemDescriptionChange(item.id, singleItem.ITEM_NAME);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f8f9fa';
                                                    setSelectedIndex(prev => ({ ...prev, [key]: index }));
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                      e.target.style.backgroundColor = 'white';
                                                    }
                                                  }}
                                                  tabIndex={-1}
                                                >
                                                  {singleItem.ITEM_NAME}
                                                </div>
                                              );
                                            })}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                // Disabled input for special rows (11-24)
                                <input
                                    type="text"
                                    className={`form-control ${['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) ? 'text-bold-inside-textbox' : ''}`}
                                    value={itemData[item.id]?.itemDesc || ''}
                                    id={`itemDesc${i}`}
                                    name={`itemDesc${i}`}
                                    disabled
                                    style={{ textAlign: 'center' }}
                                />
                              )}
                            </td>
                            <td className={`${itemData[item.id]?.itemCodeClass || ''}`} style={{ textAlign: 'center' }}>
                              {i < 10 ? (
                                // Searchable dropdown for first 10 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className={`form-control ${validationErrors[`${item.id}-itemCode`] ? 'is-invalid' : ''}`}
                                    value={itemData[item.id]?.itemCode || ''}
                                    placeholder="Item Code (7 digits)"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemCode', e.target.value);
                                      handleSearchChange(e.target.value);
                                      // Reset selected index when typing
                                      setSelectedIndex(prev => ({ ...prev, [`${item.id}-itemCode`]: -1 }));
                                    }}
                                    onFocus={(e) => {
                                      // Open dropdown on focus if it's closed (for keyboard navigation)
                                      // Use setTimeout to avoid conflict with mousedown
                                      setTimeout(() => {
                                        if (!dropdownStates[`${item.id}-itemCode`]) {
                                          openDropdown(item.id, 'itemCode');
                                        }
                                      }, 0);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const key = `${item.id}-itemCode`;
                                      // Only toggle if dropdown wasn't just opened by mousedown
                                      if (!justOpenedRef.current[key]) {
                                        toggleDropdown(item.id, 'itemCode');
                                      } else {
                                        // Clear the flag since we handled it
                                        justOpenedRef.current[key] = false;
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Open dropdown immediately on mousedown to ensure it opens on first click
                                      // This prevents the need for double-click
                                      if (!dropdownStates[`${item.id}-itemCode`]) {
                                        openDropdown(item.id, 'itemCode');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      handleKeyDown(e, item.id, 'itemCode', getFilteredItemsForRow(item.id, filteredItems));
                                    }}
                                    onBlur={(e) => {
                                      const value = e.target.value;
                                      if (value && value.length > 0 && value.length < 7) {
                                        toast.warning('Item Code must be exactly 7 digits', messages.COLOR_RED);
                                      }
                                    }}
                                  />
                                  {validationErrors[`${item.id}-itemCode`] && (
                                    <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem', position: 'absolute', zIndex: 1001 }}>
                                      {validationErrors[`${item.id}-itemCode`]}
                                    </div>
                                  )}
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemCode`] && (() => {
                                    // Filter out already selected items for this row
                                    const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                    return (
                                      <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{
                                          top: '100%',
                                          left: 0,
                                          zIndex: 1000,
                                          maxHeight: '200px',
                                          overflowY: 'auto'
                                        }}
                                      >
                                        {availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => {
                                              const key = `${item.id}-itemCode`;
                                              const isSelected = selectedIndex[key] === index;
                                              return (
                                                <div
                                                  key={index}
                                                  ref={(el) => {
                                                    if (el) {
                                                      dropdownItemRefs.current[`${key}-${index}`] = el;
                                                    }
                                                  }}
                                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                                  style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                                    outlineOffset: '-2px'
                                                  }}
                                                  onClick={() => {
                                                    handleItemCodeChange(item.id, singleItem.ASICC_CODE);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f8f9fa';
                                                    setSelectedIndex(prev => ({ ...prev, [key]: index }));
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                      e.target.style.backgroundColor = 'white';
                                                    }
                                                  }}
                                                  tabIndex={-1}
                                                >
                                                  {singleItem.ASICC_CODE}
                                                </div>
                                              );
                                            })}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                // Disabled input for special rows (11-24)
                                <input
                                  type="text"
                                  className="form-control"
                                  value={itemData[item.id]?.itemCode || ''}
                                  id={`itemCode${i}`}
                                  name={`itemCode${i}`}
                                  disabled={isDisable}
                                  maxLength="7"
                                  style={{ textAlign: 'center' }}
                                  onChange={e => handleInputChange(item.id, 'itemCode', e.target.value)}
                                  onFocus={() => handleOnFocus(item.id, 'itemCode')}
                                  onBlur={(e) => handleOnBlur(item.id, e.target.value)}
                                />
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {['9990400', '9990500', '9999900'].includes(itemData[item.id]?.itemCode) ?
                                "KWH" :
                                <input
                                  type="text"
                                  className="form-control"
                                  value={itemData[item.id]?.unitOfQty || ''}
                                  id={`unitOfQty${i}`}
                                  name={`unitOfQty${i}`}
                                  maxLength="19"
                                  disabled
                                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', textAlign: 'center' }}
                                />
                              }
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className={`form-control ${validationErrors[`${item.id}-qtyConsumed`] ? 'is-invalid' : ''} ${['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) ? 'text-bold-inside-textbox' : ''}`}
                                  value={itemData[item.id]?.itemCode === '9990100' ? totalBasicItemsQtyConsumed : (itemData[item.id]?.itemCode === '9992000' ? totalNonBasicItemsQtyConsumed : (itemData[item.id]?.itemCode === '9993000' ? totalInputQtyConsumed : (itemData[item.id]?.qtyConsumed || '')))}
                                  id={`qtyConsumed${i}`}
                                  name={`qtyConsumed${i}`}
                                  maxLength="18"
                                  style={{ textAlign: 'center' }}
                                  onChange={e => handleInputChange(item.id, 'qtyConsumed', e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) < 0) {
                                      handleInputChange(item.id, 'qtyConsumed', '0');
                                      toast.warning('Quantity cannot be negative', messages.COLOR_RED);
                                    }
                                  }}
                                  disabled={['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) || ['9920100', '9920300', '9922000', '9990100'
                                    , '9990400', '9990600', '9990800', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) && i !== 14 || i === 19}
                                  readOnly={['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode)}
                                />
                                {validationErrors[`${item.id}-qtyConsumed`] && (
                                  <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                                    {validationErrors[`${item.id}-qtyConsumed`]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className={`form-control ${validationErrors[`${item.id}-purchaseValue`] ? 'is-invalid' : ''} ${['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) ? 'text-bold-inside-textbox' : ''}`}
                                  value={itemData[item.id]?.itemCode === '9990100' ? totalBasicItemsPurchaseValue : (itemData[item.id]?.itemCode === '9992000' ? totalNonBasicItemsPurchaseValue : (itemData[item.id]?.itemCode === '9993000' ? totalInputPurchaseValue : (itemData[item.id]?.purchaseValue || '')))}
                                  id={`purchaseValue${i}`}
                                  name={`purchaseValue${i}`}
                                  maxLength="12"
                                  style={{ textAlign: 'center' }}
                                  onChange={e => handleInputChange(item.id, 'purchaseValue', e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) < 0) {
                                      handleInputChange(item.id, 'purchaseValue', '0');
                                      toast.warning('Purchase Value cannot be negative', messages.COLOR_RED);
                                    }
                                  }}
                                  disabled={['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) || ['9990400', '9999900'].includes(itemData[item.id]?.itemCode) || (i >= 13 && i <= 23)}
                                  readOnly={['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode)}
                                />
                                {validationErrors[`${item.id}-purchaseValue`] && (
                                  <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                                    {validationErrors[`${item.id}-purchaseValue`]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={itemData[item.id]?.ratePerUnit || ''}
                                id={`ratePerUnit${i}`}
                                name={`ratePerUnit${i}`}
                                maxLength="18"
                                disabled
                                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {!itemCodeArray.includes(itemData[item.id]?.itemCode) && (
                                <a
                                  href="#"
                                  className='d-block text-center'
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteSingleRow(item.id);
                                  }}
                                  title={`Delete Row ${i + 1}`}
                                >
                                  <i className="fa fa-trash"></i>
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='card mb-3'>
          <div className='card-header'><h5 className='mb-0 pb-1 pt-1' style={{ fontWeight: 'bold' }}>Control Figures from Profit and Loss</h5></div>
          <div className='card-body'>
            <table className='table table-bordered' style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {totalProfitandLossAmount.SL_No !== '' || totalProfitandLossAmount.Description !== '' || totalProfitandLossAmount.Amount !== '' ? (
                  <tr>
                    <td>{totalProfitandLossAmount.SL_No !== '' ? totalProfitandLossAmount.SL_No : ''}</td>
                    <td>{totalProfitandLossAmount.Description || ''}</td>
                    <td>
                      <input
                        type="text"
                        maxLength={12}
                        className={`form-control ${profitLossAmountError ? 'is-invalid' : ''}`}
                        value={totalProfitandLossAmount.Amount ?? ''}
                        onChange={(e) => {
                          let val = (e.target.value || '').toString();
                          if (val !== '' && !/^\d*\.?\d*$/.test(val)) return;
                          if (val.length > 12) val = val.substring(0, 12);
                          setProfitLossAmountDirty(true);
                          setProfitLossAmountError('');
                          setTotalProfitandLossAmount(prev => ({ ...prev, Amount: val }));
                        }}
                        style={{ textAlign: 'center' }}
                      />
                      {profitLossAmountError && (
                        <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                          {profitLossAmountError}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-6 remarks-section'>

            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={[]}
              filteredBlockItems={[]}
              isLoadingBlockItems={false}
              debouncedBlockItemSearch={() => { }}
              loadMoreBlockItemData={() => { }}
              hasMoreBlockItems={false}
              isLoadingMoreBlockItems={false}
              selectedItemsFromTable={selectedItemDescriptions}
              remarksList={remarksList}
              availableColumns={[5, 6]}
            />
          </div>
          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>Col-4 and 5 are Mandatory</li>
                  <li>For OTHERS item, Discription and Code Col-3 are also Mandatory</li>
                </ul>
              </div>
            </div>
          </div>


        </div>
        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-lg-8 text-center text-lg-start mb-3 mb-lg-0">
            <button
              type="button"
              className="btn btn_orange me-1"
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange me-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="reset"
              className="btn btn_orange me-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange me-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success me-1 px-3"
              onClick={addRow}
            >
              <i className="fa fa-plus-circle me-1"></i>
              Add Row
            </button>
            {/* Delete Row button commented out - using Action button for deletion
            <button
              type="button"
              className="btn btn_red px-3"
              onClick={DeleteRow}
            >
              <i className="fa fa-minus-circle me-1"></i>
              Delete Row
            </button>
            */}
          </div>
          <div className="col-lg-4 text-center text-lg-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/employmentAndLabourCost')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                // Save data first, then navigate only if successful
                const saveSuccess = await handleSaveToAPI();
                if (saveSuccess) {
                  navigate('/input-items-imported');
                }
                // If save fails, stay on current page
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
        <div className=''>
          <Remarks__list remarksData={useMemo(() => {
            return filterRemarksList(remarksList, remarksData);
          }, [remarksList, remarksData])} />
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </>
  );
}

export default InputItemsIndigenous; 